import {
  type Category,
  type Product,
  type ProductFilters,
} from '@airship-platform/airship-proposal';

type Metric = { value: number; unit: string };

function normalizeNameVariants(name: string): string[] {
  const variants = [name];

  if (name.includes('.0 ')) {
    variants.push(name.replace('.0 ', ' '));
  }

  if (name.toLowerCase().includes('btu') && name.includes(',')) {
    variants.push(...resolveBtuVariants(name));
  }

  return [...new Set(variants)];
}

function checkSpecialBtuMatching(
  name: string,
  searchable: string
): boolean | undefined {
  if (!name.toLowerCase().includes('btu') || !name.includes(',')) {
    return undefined;
  }

  const isBelow = name.toLowerCase().includes('below');
  const isAbove = name.toLowerCase().includes('above');

  if (!isBelow && !isAbove) {
    return undefined;
  }

  const withoutCommas = name.replace(/,/g, '');
  const filterMatch = withoutCommas.match(/(\d+)\s*btu/i);

  if (!filterMatch?.[1]) {
    return undefined;
  }

  const filterValue = parseInt(filterMatch[1], 10);
  return matchBtuValues(searchable, filterValue, isBelow, isAbove);
}

function resolveBtuVariants(name: string): string[] {
  const withoutCommas = name.replace(/,/g, '');
  const match = withoutCommas.match(/(\d+)(\s*btu)/i);
  if (!match?.[1]) return [];

  const num = parseInt(match[1], 10);
  const kValue = Math.floor(num / 1000);
  const nameLower = name.toLowerCase();

  const bounds = nameLower.includes('below')
    ? { lowerBound: 0, upperBound: kValue - 1 }
    : nameLower.includes('above')
      ? { lowerBound: kValue + 1, upperBound: 150 }
      : {
          lowerBound: Math.floor((num * 0.8) / 1000),
          upperBound: Math.ceil((num * 1.2) / 1000),
        };

  return Array.from(
    { length: bounds.upperBound - bounds.lowerBound + 1 },
    (_, i) => bounds.lowerBound + i
  )
    .filter((k) => k !== kValue)
    .reduce(
      (variants, k) => {
        variants.push(`${k}k btu`, `${k * 1000} btu`);
        return variants;
      },
      [`${num} btu`, ...(num >= 1000 ? [`${kValue}k btu`] : [])] as string[]
    );
}

function matchBtuValues(
  searchable: string,
  filterValue: number,
  isBelow: boolean,
  isAbove: boolean
): boolean {
  const productMatches = Array.from(
    searchable.matchAll(/(\d+[,\d]*)(?:k)?\s*btu/gi)
  );
  if (productMatches.length) {
    return productMatches.some((match) => {
      const valueStr = match[1].replace(/,/g, '');
      let value = parseInt(valueStr, 10);
      if (match[0].toLowerCase().includes('k btu')) {
        value *= 1000;
      }
      return (
        (isBelow && value < filterValue) || (isAbove && value > filterValue)
      );
    });
  }
  return false;
}

function isMetricOnRange(
  metric: Metric,
  min: Metric | undefined,
  max: Metric | undefined
): boolean {
  const isMin =
    !min ||
    (min.unit.toLowerCase().trim() === metric.unit.toLowerCase().trim() &&
      min.value <= metric.value);
  const isMax =
    !max ||
    (max.unit.toLowerCase().trim() === metric.unit.toLowerCase().trim() &&
      metric.value <= max.value);
  return isMin && isMax;
}

function createMetricFilter(
  value: string | undefined,
  unit: string | undefined
): Metric | undefined {
  if (value && unit) {
    return { value: Number(value), unit };
  }
  return undefined;
}

function nameMatcher(product: Product, names: string[]): boolean {
  const searchable = [product.name, product.description, product.model]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const sanitizedNames = [
    ...new Set(names.map((n) => n.toLowerCase()).filter(Boolean)),
  ];

  return sanitizedNames.every((name) => {
    const normalizedVariants = normalizeNameVariants(name);
    const specialBtuMatch = checkSpecialBtuMatching(name, searchable);
    if (specialBtuMatch !== undefined) {
      return specialBtuMatch;
    }
    return normalizedVariants.some((variant) =>
      searchable.includes(variant.toLowerCase())
    );
  });
}

export function queryPricebookOffline(
  products: Product[],
  filterOptions: Partial<ProductFilters>,
  categories: Category[]
): Product[] {
  const categoriesMap = new Map(categories.map((c) => [c.categoryId, c]));

  return products.filter((product) => {
    const matchesName =
      !filterOptions.names || nameMatcher(product, filterOptions.names);

    const isPricebookType =
      !filterOptions.pricebookTypes?.length ||
      filterOptions.pricebookTypes.includes(product.type);

    const matchesBrand =
      !filterOptions.brands?.length ||
      (product.type === 'EQUIPMENT' &&
        !!product.brand &&
        filterOptions.brands.includes(product.brand));

    const hasCategoryIds = product.categoryIds.length > 0;
    const filterHasCategoryIds = filterOptions.categoryIds || [];
    const matchesCategoryIds =
      hasCategoryIds &&
      (!filterHasCategoryIds.length ||
        product.categoryIds.some((id) => filterHasCategoryIds.includes(id)));

    const productCategories = product.categoryIds.flatMap(
      (id) => categoriesMap.get(id) || []
    );

    const allWithBusinessUnitIds = productCategories.every(
      (c) => c.businessUnitIds.length > 0
    );
    const categoryBusinessUnitIds = productCategories.flatMap(
      (c) => c.businessUnitIds
    );
    const matchesBusinessUnitIds =
      !filterOptions.businessUnitIds ||
      !filterOptions.businessUnitIds.length ||
      !allWithBusinessUnitIds ||
      !categoryBusinessUnitIds.length ||
      filterOptions.businessUnitIds.some((id) =>
        categoryBusinessUnitIds.includes(id)
      );

    const capacityMin = createMetricFilter(
      filterOptions.capacityMin,
      filterOptions.capacityUnit
    );
    const capacityMax = createMetricFilter(
      filterOptions.capacityMax,
      filterOptions.capacityUnit
    );
    const efficiencyMin = createMetricFilter(
      filterOptions.efficiencyMin,
      filterOptions.efficiencyUnit
    );
    const efficiencyMax = createMetricFilter(
      filterOptions.efficiencyMax,
      filterOptions.efficiencyUnit
    );

    const matchesCapacity =
      !(capacityMin || capacityMax) ||
      ((product.type === 'EQUIPMENT' || product.type === 'SERVICE') &&
        product.capacity.some((c) =>
          isMetricOnRange(c, capacityMin, capacityMax)
        ));

    const matchesEfficiency =
      !(efficiencyMin || efficiencyMax) ||
      ((product.type === 'EQUIPMENT' || product.type === 'SERVICE') &&
        product.efficiency.some((e) =>
          isMetricOnRange(e, efficiencyMin, efficiencyMax)
        ));

    return (
      isPricebookType &&
      matchesName &&
      matchesBrand &&
      matchesCategoryIds &&
      matchesBusinessUnitIds &&
      matchesCapacity &&
      matchesEfficiency
    );
  });
}
